import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  COL_L,
  COL_M,
  COL_N,
  HEADER_FILL,
  LINKED_FILL,
  NA_FILL,
  TITLE_FONT,
  VALUE_FILL,
  borderFromSpecs,
  dnSectionBorders,
  dnTitleBorders,
  formatNumber,
  formatRatio,
  getFyLabels,
  mergeBlock,
  outerMergeBorders,
  padMergeRow,
  parseBorderSpec,
  safeText,
  setCell,
  setGrey,
  setLinked,
  setPlain,
  setPrinciple5ColumnWidths,
  setWhite,
  setupPrinciple5SheetView,
  toYesNo,
  toYesNoNa,
} from "./principle5SheetHelpers.js";
import { hideRowsThrough } from "./detailSheetCommon.js";

const OTHER_ASSESSMENTS_SHEET = "Other assessments";
const OTHER_VALUE_CHAIN_SHEET = "Details of other value chain";

const PRINCIPLE_TITLE = "PRINCIPLE 5 Businesses should respect and promote human rights";

const COMPLAINT_TYPES = [
  ["Sexual Harassment", "sexualHarassemnt1"],
  ["Discrimination at workplace", "discrimination2"],
  ["Child Labour", "childLabor3"],
  ["Forced Labour/Involuntary Labour", "forcedLabor4"],
  ["Wages", "wages5"],
  ["Other human rights related issues", "other6"],
];

const ASSESSMENT_ROWS = [
  ["Child labour ", "childLabor"],
  ["Forced/involuntary labour ", "forcedLabor"],
  ["Sexual harassment", "sexualHarassment"],
  ["Discrimination at workplace", "discrimination"],
  ["Wages", "wages"],
];

const VALUE_CHAIN_ROWS = [
  ["Sexual harassment ", "sexualHarassment"],
  ["Discrimination at workplace ", "discrimination"],
  ["Child Labour ", "childLabor"],
  ["Forced Labour/Involuntary Labour ", "forcedLabor"],
  ["Wages ", "wages"],
];

const MEDIAN_ROWS = [
  ["Board of Directors (BoD)", "boardOfDirectors"],
  ["Key Managerial Personnel", "keyManagementPersonnel"],
  ["Employees other than BoD and KMP", "otherEmployees"],
  ["Workers", "workers"],
];

const SECTION_FONT = { name: "Calibri", size: 12, bold: true };
const THIN_ALL = "tttt";

function writeMergedLabel(ws, row, startCol, endCol, text, specs, options = {}) {
  for (let col = startCol; col <= endCol; col += 1) {
    setCell(ws, row, col, col === startCol ? text : "", {
      bold: options.bold,
      font: options.font,
      align: options.align || "left",
      vertical: options.vertical || "middle",
      fill: options.fill,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, startCol, endCol, specs);
  for (let col = endCol + 1; col <= COL_N; col += 1) {
    const border = borderFromSpecs(specs, col);
    if (border) ws.getCell(row, col).border = border;
  }
  if (options.height != null) ws.getRow(row).height = options.height;
}

function paintTrailingGrey(ws, row, fromCol = COL_K) {
  for (let col = fromCol; col <= COL_N; col += 1) {
    setGrey(ws, row, col, parseBorderSpec(col === COL_N ? "---m" : "----"));
  }
}

function addPrincipleTitle(ws) {
  const specs = dnTitleBorders();
  for (let col = COL_D; col <= COL_N; col += 1) {
    setCell(ws, 8, col, col === COL_D ? PRINCIPLE_TITLE : "", {
      font: TITLE_FONT,
      align: "center",
      fill: HEADER_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, 8, COL_D, COL_N, specs);
  ws.getRow(8).height = 30;
}

function addSectionBand(ws, row, title) {
  const specs = dnSectionBorders();
  for (let col = COL_D; col <= COL_N; col += 1) {
    setCell(ws, row, col, col === COL_D ? title : "", {
      font: SECTION_FONT,
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, COL_D, COL_N, specs);
  ws.getRow(row).height = 20.15;
}

function addIntroMerged(ws, row, text, endCol = COL_J, options = {}) {
  const specs = outerMergeBorders(endCol, {
    first: options.first || "-mt-",
    middle: options.middle || "--t-",
    last: options.last || "--tt",
    edge: options.edge || "---m",
  });
  writeMergedLabel(ws, row, COL_D, endCol, text, specs, {
    align: "left",
    vertical: "middle",
    height: options.height,
  });
  for (let col = endCol + 1; col < COL_N; col += 1) {
    setGrey(ws, row, col, parseBorderSpec("----"));
  }
  if (endCol < COL_N) {
    setGrey(ws, row, COL_N, parseBorderSpec(options.edge || "---m"));
  }
}

function wagePerson(block, key) {
  return block?.[key] || {};
}

function wageVals(person) {
  return {
    total: formatNumber(person?.total),
    eqNo: formatNumber(person?.equalWageMin?.number),
    eqPct: formatRatio(person?.equalWageMin?.percentage),
    moreNo: formatNumber(person?.equalWageMax?.number),
    morePct: formatRatio(person?.equalWageMax?.percentage),
  };
}

function writeTrainingHeader(ws, row, fyCurrent, fyPrevious) {
  // R11 Category + FY/PY bands (Category spans R11-12)
  mergeBlock(ws, row, COL_D, row + 1, COL_D, "Category", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tm-t"),
  });
  ws.getCell(row + 1, COL_D).border = parseBorderSpec("-mtt");
  ws.getCell(row + 1, COL_D).fill = NA_FILL;

  mergeBlock(ws, row, COL_E, row, COL_G, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  mergeBlock(ws, row, COL_H, row, COL_J, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  paintTrailingGrey(ws, row);

  const sub = [
    [COL_E, "Total (A)"],
    [COL_F, "No. of employees/workers covered (B)"],
    [COL_G, "% (B\n/ A)"],
    [COL_H, "Total (C)"],
    [COL_I, "No. of employees/workers covered (D)"],
    [COL_J, "% (D / C)"],
  ];
  sub.forEach(([col, label]) => {
    setCell(ws, row + 1, col, label, {
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
  });
  paintTrailingGrey(ws, row + 1);
  ws.getRow(row + 1).height = 43.5;
}

function writeTrainingBand(ws, row, label) {
  const specs = outerMergeBorders(COL_J, {
    first: "tmt-",
    middle: "t-t-",
    last: "t-tt",
    edge: "---m",
  });
  for (let col = COL_D; col <= COL_J; col += 1) {
    setCell(ws, row, col, col === COL_D ? label : "", {
      align: "left",
      fill: NA_FILL,
      bordered: false,
      border: borderFromSpecs(specs, col),
    });
  }
  padMergeRow(ws, row, COL_D, COL_J, specs);
  paintTrailingGrey(ws, row);
}

function writeTrainingDataRow(ws, row, label, curr, prev, { totalRow = false, last = false } = {}) {
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
    border: parseBorderSpec(totalRow ? "tmt-" : "tmtt"),
  });

  const cTotal = formatNumber(curr?.total);
  const cNo = formatNumber(curr?.noOfEmployee);
  const cPct = formatRatio(curr?.percentage);
  const pTotal = formatNumber(prev?.total);
  const pNo = formatNumber(prev?.noOfEmployee);
  const pPct = formatRatio(prev?.percentage);

  const fillTotal = totalRow ? LINKED_FILL : LINKED_FILL;
  const fillNo = totalRow ? LINKED_FILL : VALUE_FILL;

  setCell(ws, row, COL_E, cTotal, {
    align: "center",
    fill: fillTotal,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_F, cNo, {
    align: "center",
    fill: fillNo,
    bordered: false,
    border: parseBorderSpec(totalRow ? "t-t-" : "t-t-"),
  });
  setCell(ws, row, COL_G, cPct, {
    align: "center",
    fill: LINKED_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_H, pTotal, {
    align: "center",
    fill: totalRow ? LINKED_FILL : VALUE_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_I, pNo, {
    align: "center",
    fill: fillNo,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  setCell(ws, row, COL_J, pPct, {
    align: "center",
    fill: LINKED_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });

  if (last) {
    for (let col = COL_K; col < COL_N; col += 1) {
      setGrey(ws, row, col, parseBorderSpec("--t-"));
    }
    setGrey(ws, row, COL_N, parseBorderSpec("--tm"));
  } else {
    paintTrailingGrey(ws, row);
  }
}

function writeWageTableHeader(ws, startRow, fyCurrent, fyPrevious) {
  // R22-24: Category spans 3 rows
  mergeBlock(ws, startRow, COL_D, startRow + 2, COL_D, "Category", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });

  mergeBlock(ws, startRow, COL_E, startRow, COL_I, fyCurrent, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, startRow, COL_J, startRow, COL_N, fyPrevious, {
    bold: true,
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttm"),
  });

  mergeBlock(ws, startRow + 1, COL_E, startRow + 2, COL_E, "Total (A)", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, startRow + 1, COL_F, startRow + 1, COL_G, "Equal to Minimum Wage", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, startRow + 1, COL_H, startRow + 1, COL_I, "More than Minimum Wage", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, startRow + 1, COL_J, startRow + 2, COL_J, "Total (D)", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, startRow + 1, COL_K, startRow + 1, COL_L, "Equal to Minimum Wage", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, startRow + 1, COL_M, startRow + 1, COL_N, "More than Minimum Wage", {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tttm"),
  });

  const r3 = startRow + 2;
  const labels = [
    [COL_F, "No.\n(B)"],
    [COL_G, "% (B /A)"],
    [COL_H, "No.\n(C)"],
    [COL_I, "% (C /A)"],
    [COL_K, "No.\n(E)"],
    [COL_L, "% (E /D)"],
    [COL_M, "No.\n(F)"],
    [COL_N, "% (F /D)"],
  ];
  labels.forEach(([col, label]) => {
    setCell(ws, r3, col, label, {
      align: "center",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(col === COL_N ? "tttm" : THIN_ALL),
    });
  });
}

function writeWageGroupHeader(ws, row, label) {
  for (let col = COL_D; col <= COL_N; col += 1) {
    setCell(ws, row, col, col === COL_D ? label : "", {
      bold: true,
      align: "left",
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec(col === COL_N ? "tt-m" : col === COL_D ? "tmtt" : "tt-t"),
    });
  }
  padMergeRow(ws, row, COL_D, COL_N, dnSectionBorders().map((s, i) => (i === 0 ? "tmtt" : i === 10 ? "tt-m" : "tt-t")));
}

function writeWageCategoryHeader(ws, row, label, currTotal, prevTotal) {
  const c = wageVals(currTotal);
  const p = wageVals(prevTotal);
  setCell(ws, row, COL_D, label, {
    align: "left",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tmt-"),
  });
  setCell(ws, row, COL_E, c.total, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("tt-t"),
  });
  setCell(ws, row, COL_F, c.eqNo, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  setCell(ws, row, COL_G, c.eqPct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_H, c.moreNo, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("t-t-"),
  });
  setCell(ws, row, COL_I, c.morePct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_J, p.total, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("t-tt"),
  });
  setCell(ws, row, COL_K, p.eqNo, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("ttt-"),
  });
  setCell(ws, row, COL_L, p.eqPct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setCell(ws, row, COL_M, p.moreNo, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec("t-t-"),
  });
  setCell(ws, row, COL_N, p.morePct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
}

function writeWageGenderRow(ws, row, label, curr, prev) {
  const c = wageVals(curr);
  const p = wageVals(prev);
  setCell(ws, row, COL_D, label, {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  setLinked(ws, row, COL_E, c.total, parseBorderSpec(THIN_ALL));
  setWhite(ws, row, COL_F, c.eqNo, parseBorderSpec("--t-"));
  setCell(ws, row, COL_G, c.eqPct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setWhite(ws, row, COL_H, c.moreNo, parseBorderSpec("--t-"));
  setCell(ws, row, COL_I, c.morePct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setWhite(ws, row, COL_J, p.total, parseBorderSpec("--tt"));
  setWhite(ws, row, COL_K, p.eqNo, parseBorderSpec("-tt-"));
  setCell(ws, row, COL_L, p.eqPct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  setWhite(ws, row, COL_M, p.moreNo, parseBorderSpec("--t-"));
  setCell(ws, row, COL_N, p.morePct, {
    align: "center",
    fill: NA_FILL,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
}

function writeLabelValueRow(ws, row, label, value, labelEnd, valueCol, options = {}) {
  const valueEnd = options.valueEnd || valueCol;
  const specs = [];
  for (let col = COL_D; col <= COL_N; col += 1) {
    if (col === COL_D) specs.push(options.first || "tmtt");
    else if (col < labelEnd) specs.push(options.midLabel || "-ttt");
    else if (col === labelEnd) specs.push(options.lastLabel || "-ttt");
    else if (col === valueCol) specs.push(options.valueBorder || "-ttt");
    else if (col === COL_N) specs.push("---m");
    else specs.push("----");
  }
  writeMergedLabel(ws, row, COL_D, labelEnd, label, specs, {
    align: "left",
    vertical: "middle",
    height: options.height,
  });
  if (valueEnd > valueCol) {
    for (let col = valueCol; col <= valueEnd; col += 1) {
      setCell(ws, row, col, col === valueCol ? value : "", {
        align: options.valueAlign || "center",
        fill: options.valueFill || VALUE_FILL,
        bordered: false,
        border: parseBorderSpec(options.valueBorder || THIN_ALL),
        link: options.link,
      });
    }
    padMergeRow(ws, row, valueCol, valueEnd, null);
  } else {
    setCell(ws, row, valueCol, value, {
      align: options.valueAlign || "center",
      fill: options.valueFill || VALUE_FILL,
      bordered: false,
      border: parseBorderSpec(options.valueBorder || THIN_ALL),
      link: options.link,
      vertical: "middle",
    });
  }
  for (let col = Math.max(labelEnd, valueEnd) + 1; col <= COL_N; col += 1) {
    if (col === valueCol) continue;
    if (col > valueEnd || col < valueCol) {
      setGrey(ws, row, col, parseBorderSpec(col === COL_N ? "---m" : "----"));
    }
  }
  // Grey padding between labelEnd+1 and valueCol-1 if gap
  for (let col = labelEnd + 1; col < valueCol; col += 1) {
    setGrey(ws, row, col, parseBorderSpec("----"));
  }
  for (let col = valueEnd + 1; col <= COL_N; col += 1) {
    setGrey(ws, row, col, parseBorderSpec(col === COL_N ? "---m" : "----"));
  }
}

function medianFields(item = {}) {
  return {
    maleNo: formatNumber(item.numberofMale ?? item.numberofmale),
    maleMed: formatNumber(item.medianRenumerationMale ?? item.maleWages),
    femaleNo: formatNumber(item.numberofFemale ?? item.numberofFemale),
    femaleMed: formatNumber(item.medianRenumerationFemale ?? item.femaleWages),
    otherNo: formatNumber(item.numberofOther ?? item.numberofother ?? item.otherNumber),
    otherMed: formatNumber(
      item.medianRenumerationOther ?? item.otherWages ?? item.medianOther
    ),
  };
}

export function buildPrinciple5Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 5");
  setPrinciple5ColumnWidths(ws);
  setupPrinciple5SheetView(workbook, ws);
  hideRowsThrough(ws, 5);

  const principle = sectionC?.principle5 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const { current: fyCurrent, previous: fyPrevious } = getFyLabels(reportingYear);

  // —— R8–9 title / essential ——
  addPrincipleTitle(ws);
  addSectionBand(ws, 9, "Essential Indicators");

  // —— R10 Q1 intro ——
  addIntroMerged(
    ws,
    10,
    "1. Employees and workers who have been provided training on human rights issues and policy(ies) of the entity, in the following format:",
    COL_J,
    { first: "-mt-", middle: "--t-", last: "--tt" }
  );

  // —— R11–12 training headers ——
  writeTrainingHeader(ws, 11, fyCurrent, fyPrevious);

  // —— R13–16 Employees ——
  const emp = essential?.humanRightsTraining?.employee || {};
  writeTrainingBand(ws, 13, "Employees");
  writeTrainingDataRow(
    ws,
    14,
    "Permanent",
    emp?.currentFinancialYear?.permanentEmployees,
    emp?.previousFinancialYear?.permanentEmployees
  );
  writeTrainingDataRow(
    ws,
    15,
    "Other than permanent",
    emp?.currentFinancialYear?.otherThanPermanent,
    emp?.previousFinancialYear?.otherThanPermanent
  );
  writeTrainingDataRow(
    ws,
    16,
    "Total Employees",
    emp?.currentFinancialYear?.total,
    emp?.previousFinancialYear?.total,
    { totalRow: true }
  );

  // —— R17–20 Workers ——
  const wrk = essential?.humanRightsTraining?.workers || {};
  writeTrainingBand(ws, 17, "Workers");
  writeTrainingDataRow(
    ws,
    18,
    "Permanent",
    wrk?.currentFinancialYear?.permanentEmployees,
    wrk?.previousFinancialYear?.permanentEmployees
  );
  writeTrainingDataRow(
    ws,
    19,
    "Other than permanent",
    wrk?.currentFinancialYear?.otherThanPermanent,
    wrk?.previousFinancialYear?.otherThanPermanent
  );
  writeTrainingDataRow(
    ws,
    20,
    "Total Workers",
    wrk?.currentFinancialYear?.total,
    wrk?.previousFinancialYear?.total,
    { totalRow: true, last: true }
  );

  // —— R21 Q2 intro ——
  {
    const specs = dnSectionBorders();
    writeMergedLabel(
      ws,
      21,
      COL_D,
      COL_N,
      "2. Details of minimum wages paid to employees and workers, in the following format:",
      specs,
      { align: "left" }
    );
  }

  // —— R22–24 wage headers ——
  writeWageTableHeader(ws, 22, fyCurrent, fyPrevious);

  const wages = essential?.minimumWages || {};
  const currW = wages.currentFinancialYear || {};
  const prevW = wages.previousFinancialYear || {};

  // —— R25–33 Employees wages ——
  writeWageGroupHeader(ws, 25, "Employees");
  writeWageCategoryHeader(
    ws,
    26,
    "Permanent",
    wagePerson(currW.permanentEmployees, "totalAddition"),
    wagePerson(prevW.permanentEmployees, "totalAddition")
  );
  writeWageGenderRow(
    ws,
    27,
    "Male",
    wagePerson(currW.permanentEmployees, "male"),
    wagePerson(prevW.permanentEmployees, "male")
  );
  writeWageGenderRow(
    ws,
    28,
    "Female",
    wagePerson(currW.permanentEmployees, "female"),
    wagePerson(prevW.permanentEmployees, "female")
  );
  writeWageGenderRow(
    ws,
    29,
    "Other",
    wagePerson(currW.permanentEmployees, "other"),
    wagePerson(prevW.permanentEmployees, "other")
  );
  writeWageCategoryHeader(
    ws,
    30,
    "Other than Permanent",
    wagePerson(currW.otherThanPermanentEmployees, "totalAddition"),
    wagePerson(prevW.otherThanPermanentEmployees, "totalAddition")
  );
  writeWageGenderRow(
    ws,
    31,
    "Male",
    wagePerson(currW.otherThanPermanentEmployees, "male"),
    wagePerson(prevW.otherThanPermanentEmployees, "male")
  );
  writeWageGenderRow(
    ws,
    32,
    "Female",
    wagePerson(currW.otherThanPermanentEmployees, "female"),
    wagePerson(prevW.otherThanPermanentEmployees, "female")
  );
  writeWageGenderRow(
    ws,
    33,
    "Other",
    wagePerson(currW.otherThanPermanentEmployees, "other"),
    wagePerson(prevW.otherThanPermanentEmployees, "other")
  );

  // —— R34–42 Workers wages ——
  writeWageGroupHeader(ws, 34, "Workers");
  writeWageCategoryHeader(
    ws,
    35,
    "Permanent",
    wagePerson(currW.permanentWorkers, "totalAddition"),
    wagePerson(prevW.permanentWorkers, "totalAddition")
  );
  writeWageGenderRow(
    ws,
    36,
    "Male",
    wagePerson(currW.permanentWorkers, "male"),
    wagePerson(prevW.permanentWorkers, "male")
  );
  writeWageGenderRow(
    ws,
    37,
    "Female",
    wagePerson(currW.permanentWorkers, "female"),
    wagePerson(prevW.permanentWorkers, "female")
  );
  writeWageGenderRow(
    ws,
    38,
    "Other",
    wagePerson(currW.permanentWorkers, "other"),
    wagePerson(prevW.permanentWorkers, "other")
  );
  writeWageCategoryHeader(
    ws,
    39,
    "Other than Permanent",
    wagePerson(currW.otherThanPermanentWorkers, "totalAddition"),
    wagePerson(prevW.otherThanPermanentWorkers, "totalAddition")
  );
  writeWageGenderRow(
    ws,
    40,
    "Male",
    wagePerson(currW.otherThanPermanentWorkers, "male"),
    wagePerson(prevW.otherThanPermanentWorkers, "male")
  );
  writeWageGenderRow(
    ws,
    41,
    "Female",
    wagePerson(currW.otherThanPermanentWorkers, "female"),
    wagePerson(prevW.otherThanPermanentWorkers, "female")
  );
  writeWageGenderRow(
    ws,
    42,
    "Other",
    wagePerson(currW.otherThanPermanentWorkers, "other"),
    wagePerson(prevW.otherThanPermanentWorkers, "other")
  );

  // —— R43–44 Q3 ——
  addIntroMerged(
    ws,
    43,
    "3. Details of remuneration/salary/wages, in the following format:",
    COL_J,
    { first: "tmt-", middle: "t-t-", last: "t-tt", edge: "t--m" }
  );
  // fix trailing for R43 — top border on grey
  for (let col = COL_K; col < COL_N; col += 1) {
    setGrey(ws, 43, col, parseBorderSpec("t---"));
  }
  setGrey(ws, 43, COL_N, parseBorderSpec("t--m"));

  {
    const specs = outerMergeBorders(COL_J, {
      first: "tmt-",
      middle: "t-t-",
      last: "t-tt",
      edge: "---m",
    });
    writeMergedLabel(ws, 44, COL_D, COL_J, "a. Median remuneration / wages:", specs, {
      align: "left",
    });
    paintTrailingGrey(ws, 44);
  }

  // —— R45–46 median headers ——
  mergeBlock(ws, 45, COL_D, 46, COL_D, "", {
    bordered: false,
    border: parseBorderSpec("tm-t"),
  });
  ws.getCell(46, COL_D).border = parseBorderSpec("-mtt");
  mergeBlock(ws, 45, COL_E, 45, COL_F, "Male", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, 45, COL_G, 45, COL_H, "Female", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, 45, COL_I, 45, COL_J, "Other", {
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  paintTrailingGrey(ws, 45);

  const medHeaders = [
    [COL_E, "Number"],
    [COL_F, "Median remuneration/ salary/ wages of respective category"],
    [COL_G, "Number"],
    [COL_H, "Median remuneration/ salary/ wages of\nrespective category"],
    [COL_I, "Number"],
    [COL_J, "Median remuneration/ salary/ wages of\nrespective category"],
  ];
  medHeaders.forEach(([col, label]) => {
    setCell(ws, 46, col, label, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
  });
  paintTrailingGrey(ws, 46);
  ws.getRow(46).height = 70;

  const median = essential?.remunerationDetails?.medianRemuneration || {};
  MEDIAN_ROWS.forEach(([label, key], idx) => {
    const row = 47 + idx;
    const m = medianFields(median[key]);
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setWhite(ws, row, COL_E, m.maleNo, parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_F, m.maleMed, parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_G, m.femaleNo, parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_H, m.femaleMed, parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_I, m.otherNo, parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_J, m.otherMed, parseBorderSpec("t-tt"));
    paintTrailingGrey(ws, row);
  });

  // —— R51–55 gross wages ——
  {
    const specs = outerMergeBorders(COL_J, {
      first: "tmtt",
      middle: THIN_ALL,
      last: THIN_ALL,
      edge: "---m",
    });
    writeMergedLabel(ws, 51, COL_D, COL_J, "b. Gross wages paid to females:", specs, {
      align: "left",
    });
    paintTrailingGrey(ws, 51);
  }

  setCell(ws, 52, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  mergeBlock(ws, 52, COL_E, 52, COL_G, fyCurrent, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, 52, COL_H, 52, COL_J, fyPrevious, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  paintTrailingGrey(ws, 52);

  const rem = essential?.remunerationDetails || {};
  const femalePct = rem.femalePercentage || {};
  const grossFem = rem.grossWagesPaidToFemales || rem.grossWagesFemales || {};
  const totalWages = rem.totalWages || {};

  const grossRows = [
    [
      53,
      "Gross wages paid to females",
      formatNumber(grossFem.currentFinancialYear ?? grossFem.current),
      formatNumber(grossFem.previousFinancialYear ?? grossFem.previous),
      VALUE_FILL,
    ],
    [
      54,
      "Total wages",
      formatNumber(totalWages.currentFinancialYear ?? totalWages.current),
      formatNumber(totalWages.previousFinancialYear ?? totalWages.previous),
      VALUE_FILL,
    ],
    [
      55,
      "Gross wages paid to females\n(Gross wages paid to females as % of total wages)",
      formatRatio(femalePct.currentFinancialYear),
      formatRatio(femalePct.previousFinancialYear),
      LINKED_FILL,
    ],
  ];
  grossRows.forEach(([row, label, curr, prev, fill]) => {
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
      vertical: "middle",
    });
    mergeBlock(ws, row, COL_E, row, COL_G, curr, {
      align: "center",
      fill,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    mergeBlock(ws, row, COL_H, row, COL_J, prev, {
      align: "center",
      fill,
      bordered: false,
      border: parseBorderSpec(row === 54 ? "--tt" : THIN_ALL),
    });
    paintTrailingGrey(ws, row);
  });
  ws.getRow(55).height = 45;

  // —— R56 Q4 ——
  writeLabelValueRow(
    ws,
    56,
    "4. Do you have a focal point (Individual/ Committee) responsible for addressing human rights impacts or issues caused or contributed to by the business?",
    toYesNo(essential?.humanRightsFocalPoint?.status),
    COL_G,
    COL_H,
    { height: 33.75, first: "tmtt", midLabel: "-ttt", lastLabel: "-ttt" }
  );

  // —— R57 Q5 ——
  writeLabelValueRow(
    ws,
    57,
    "5. Describe the internal mechanisms in place to redress grievances related to human rights issues.",
    safeText(essential?.internalMechanismsHumanRightsGrievances),
    COL_G,
    COL_H,
    {
      height: 30,
      first: "tmt-",
      midLabel: "t-t-",
      lastLabel: "t-tt",
      valueBorder: THIN_ALL,
      valueAlign: "left",
    }
  );

  // —— R58–66 Complaints ——
  {
    const specs = outerMergeBorders(COL_J, {
      first: "tmtt",
      middle: THIN_ALL,
      last: THIN_ALL,
      edge: "---m",
    });
    writeMergedLabel(
      ws,
      58,
      COL_D,
      COL_J,
      "6. Number of Complaints on the following made by employees and workers:",
      specs,
      { align: "left", height: 20.15 }
    );
    paintTrailingGrey(ws, 58);
  }

  mergeBlock(ws, 59, COL_D, 60, COL_D, "", {
    bordered: false,
    border: parseBorderSpec("tmtt"),
  });
  mergeBlock(ws, 59, COL_E, 59, COL_G, fyCurrent, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, 59, COL_H, 59, COL_J, fyPrevious, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  paintTrailingGrey(ws, 59);
  ws.getRow(59).height = 20.15;

  const complaintHeaders = [
    [COL_E, "Filed during the year", THIN_ALL],
    [COL_F, "Pending resolution at the end of year", THIN_ALL],
    [COL_G, "Remarks", "tt-t"],
    [COL_H, "Filed during the year", THIN_ALL],
    [COL_I, "Pending resolution at the end of year", THIN_ALL],
    [COL_J, "Remarks", "tt-t"],
  ];
  complaintHeaders.forEach(([col, label, border]) => {
    setCell(ws, 60, col, label, {
      align: "center",
      bordered: false,
      border: parseBorderSpec(border),
    });
  });
  paintTrailingGrey(ws, 60);
  ws.getRow(60).height = 29;

  const complaints = essential?.complaintsFiled || {};
  const currC = complaints.currentFinancialYear1 || {};
  const prevC = complaints.previousFinancialYear1 || {};
  COMPLAINT_TYPES.forEach(([label, key], idx) => {
    const row = 61 + idx;
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
    });
    setWhite(ws, row, COL_E, formatNumber(currC[key]?.fliedYear), parseBorderSpec(THIN_ALL));
    setWhite(ws, row, COL_F, formatNumber(currC[key]?.pending), parseBorderSpec("ttt-"));
    setPlain(ws, row, COL_G, safeText(currC[key]?.remark), parseBorderSpec(THIN_ALL), "left");
    setWhite(ws, row, COL_H, formatNumber(prevC[key]?.fliedYear), parseBorderSpec("t-tt"));
    setWhite(ws, row, COL_I, formatNumber(prevC[key]?.pending), parseBorderSpec("ttt-"));
    setWhite(ws, row, COL_J, safeText(prevC[key]?.remark), parseBorderSpec(THIN_ALL), "left");
    paintTrailingGrey(ws, row);
    ws.getRow(row).height = 30;
  });

  // —— R67–72 POSH ——
  {
    const specs = outerMergeBorders(COL_J, {
      first: "tmtt",
      middle: THIN_ALL,
      last: "-ttt",
      edge: "---m",
    });
    writeMergedLabel(
      ws,
      67,
      COL_D,
      COL_J,
      "7. Complaints filed under the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013, in the following format:",
      specs,
      { align: "left", height: 20.15 }
    );
    paintTrailingGrey(ws, 67);
  }

  setCell(ws, 68, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  mergeBlock(ws, 68, COL_E, 68, COL_G, fyCurrent, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  mergeBlock(ws, 68, COL_H, 68, COL_J, fyPrevious, {
    bold: true,
    align: "center",
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  paintTrailingGrey(ws, 68);

  const posh = essential?.complaintsFiledUnderSexualHarassemnt || {};
  const poshRows = [
    [
      69,
      "i) Total Complaints reported under Sexual Harassment on of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013 (POSH)",
      formatNumber(posh?.totalComplaints?.currentFinancialYear),
      formatNumber(posh?.totalComplaints?.previousFinancialYear),
      VALUE_FILL,
      65.15,
    ],
    [
      70,
      "ii) Average number of female employees/workers at the beginning of the year and as at end of the year",
      formatNumber(
        posh?.averageFemaleEmployees?.currentFinancialYear ??
          posh?.averageNumberOfFemaleEmployees?.currentFinancialYear
      ),
      formatNumber(
        posh?.averageFemaleEmployees?.previousFinancialYear ??
          posh?.averageNumberOfFemaleEmployees?.previousFinancialYear
      ),
      VALUE_FILL,
      43.5,
    ],
    [
      71,
      "iii) Complaints on POSH as a % of female employees / workers",
      formatRatio(posh?.complaintsFemaleEmployee?.currentFinancialYear),
      formatRatio(posh?.complaintsFemaleEmployee?.previousFinancialYear),
      LINKED_FILL,
      29,
    ],
    [
      72,
      "iv) Complaints on POSH upheld",
      formatNumber(posh?.poshUpheld?.currentFinancialYear),
      formatNumber(posh?.poshUpheld?.previousFinancialYear),
      VALUE_FILL,
      null,
    ],
  ];
  poshRows.forEach(([row, label, curr, prev, fill, height]) => {
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmtt"),
      vertical: "middle",
    });
    mergeBlock(ws, row, COL_E, row, COL_G, curr, {
      align: "center",
      fill,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    mergeBlock(ws, row, COL_H, row, COL_J, prev, {
      align: "center",
      fill,
      bordered: false,
      border: parseBorderSpec(THIN_ALL),
    });
    paintTrailingGrey(ws, row);
    if (height) ws.getRow(row).height = height;
  });

  // —— R73 Q8 ——
  writeLabelValueRow(
    ws,
    73,
    "8. Mechanisms to prevent adverse consequences to the complainant in discrimination and harassment cases.",
    safeText(essential?.mechanismsToPreventAdverseConsequences),
    COL_H,
    COL_I,
    { height: 30, valueAlign: "left" }
  );

  // —— R74 Q9 ——
  writeMergedLabel(
    ws,
    74,
    COL_D,
    COL_G,
    "9. Do human rights requirements form part of your business agreements and contracts? (Yes/No/NA)",
    outerMergeBorders(COL_G, {
      first: "tmt-",
      middle: "t-t-",
      last: "t-t-",
      edge: "---m",
    }),
    { align: "left", height: 30 }
  );
  setWhite(
    ws,
    74,
    COL_H,
    toYesNoNa(essential?.humanRightsRequirementsInAgreements?.action),
    parseBorderSpec(THIN_ALL)
  );
  setGrey(ws, 74, COL_I, parseBorderSpec(THIN_ALL));
  for (let col = COL_J; col < COL_N; col += 1) {
    setGrey(ws, 74, col, parseBorderSpec("----"));
  }
  setGrey(ws, 74, COL_N, parseBorderSpec("---m"));

  // —— R75–82 Assessments ——
  {
    const specs = outerMergeBorders(COL_G, {
      first: "tmt-",
      middle: "t-t-",
      last: "t-tt",
      edge: "---m",
    });
    writeMergedLabel(ws, 75, COL_D, COL_G, "10. Assessments for the year:", specs, {
      align: "left",
      height: 20.15,
    });
    for (let col = COL_H; col < COL_N; col += 1) {
      setGrey(ws, 75, col, parseBorderSpec("----"));
    }
    setGrey(ws, 75, COL_N, parseBorderSpec("---m"));
  }

  setCell(ws, 76, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  mergeBlock(
    ws,
    76,
    COL_E,
    76,
    COL_G,
    "% of your plants and offices that were assessed (by entity or statutory authorities or third parties)",
    {
      align: "center",
      bordered: false,
      border: parseBorderSpec("tt--"),
    }
  );
  for (let col = COL_H; col < COL_N; col += 1) {
    setGrey(ws, 76, col, parseBorderSpec("----"));
  }
  setGrey(ws, 76, COL_N, parseBorderSpec("---m"));
  ws.getRow(76).height = 39;

  const assessments = essential?.assessmentsForYear || {};
  ASSESSMENT_ROWS.forEach(([label, key], idx) => {
    const row = 77 + idx;
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmt-"),
    });
    mergeBlock(ws, row, COL_E, row, COL_G, formatRatio(assessments?.[key]?.plantsOffices), {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
      border: parseBorderSpec("ttt-"),
    });
    for (let col = COL_H; col < COL_N; col += 1) {
      setGrey(ws, row, col, parseBorderSpec("----"));
    }
    setGrey(ws, row, COL_N, parseBorderSpec("---m"));
    ws.getRow(row).height = 20.15;
  });

  // R82 Others + Add Details
  setCell(ws, 82, COL_D, "Others – please specify", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tm--"),
  });
  setCell(ws, 82, COL_E, "Add Details", {
    align: "center",
    link: `#'${OTHER_ASSESSMENTS_SHEET}'!D8`,
    bordered: false,
    border: parseBorderSpec("-ttt"),
  });
  for (let col = COL_F; col < COL_N; col += 1) {
    setGrey(ws, 82, col, parseBorderSpec("----"));
  }
  setGrey(ws, 82, COL_N, parseBorderSpec("---m"));
  ws.getRow(82).height = 20.15;

  // —— R83 Q11 ——
  {
    const specs = outerMergeBorders(COL_J, {
      first: "tmt-",
      middle: "t-t-",
      last: "t-tt",
      edge: "--tm",
    });
    writeMergedLabel(
      ws,
      83,
      COL_D,
      COL_J,
      "11. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 10 above.",
      specs,
      { align: "left", height: 30 }
    );
    setWhite(
      ws,
      83,
      COL_K,
      safeText(essential?.correctiveActionsForAssessments),
      parseBorderSpec(THIN_ALL),
      "left"
    );
    for (let col = COL_L; col < COL_N; col += 1) {
      setGrey(ws, 83, col, parseBorderSpec("--t-"));
    }
    setGrey(ws, 83, COL_N, parseBorderSpec("--tm"));
  }

  // —— R84 Leadership ——
  addSectionBand(ws, 84, "Leadership Indicators");

  // —— R85–87 leadership text/yes ——
  writeLabelValueRow(
    ws,
    85,
    "1. Details of a business process being modified / introduced as a result of addressing human rights grievances/complaints.",
    safeText(leadership?.businessProcessBeingModified),
    COL_J,
    COL_K,
    {
      height: 30,
      first: "-mtt",
      midLabel: "-ttt",
      lastLabel: "-ttt",
      valueAlign: "left",
      valueFill: undefined,
    }
  );
  // R85 value has no white fill in reference (f=none) — leave plain
  setPlain(
    ws,
    85,
    COL_K,
    safeText(leadership?.businessProcessBeingModified),
    parseBorderSpec("-ttt"),
    "left"
  );

  writeLabelValueRow(
    ws,
    86,
    "2. Details of the scope and coverage of any Human rights due-diligence conducted",
    safeText(leadership?.humanRightsDueDiligence),
    COL_J,
    COL_K,
    { height: 30, valueAlign: "left", valueFill: undefined }
  );
  setPlain(
    ws,
    86,
    COL_K,
    safeText(leadership?.humanRightsDueDiligence),
    parseBorderSpec(THIN_ALL),
    "left"
  );

  writeLabelValueRow(
    ws,
    87,
    "3. Is the premise/office of the entity accessible to differently abled visitors, as per the requirements of the Rights of Persons with Disabilities Act, 2016?",
    safeText(leadership?.accessibilityToDifferentlyAbled) ||
      toYesNo(leadership?.accessibilityToDifferentlyAbled),
    COL_J,
    COL_K,
    { height: 30 }
  );

  // —— R88–95 value chain ——
  {
    const specs = outerMergeBorders(COL_G, {
      first: "-mt-",
      middle: "--t-",
      last: "--tt",
      edge: "---m",
    });
    writeMergedLabel(
      ws,
      88,
      COL_D,
      COL_G,
      "4. Details on assessment of value chain partners:",
      specs,
      { align: "left", height: 30 }
    );
    for (let col = COL_H; col < COL_N; col += 1) {
      setGrey(ws, 88, col, parseBorderSpec("----"));
    }
    setGrey(ws, 88, COL_N, parseBorderSpec("---m"));
  }

  setCell(ws, 89, COL_D, "", { bordered: false, border: parseBorderSpec("tmtt") });
  mergeBlock(
    ws,
    89,
    COL_E,
    89,
    COL_G,
    "% of value chain partners (by value of business done with such partners) that were assessed",
    {
      align: "center",
      bordered: false,
      border: parseBorderSpec("tt-t"),
    }
  );
  for (let col = COL_H; col < COL_N; col += 1) {
    setGrey(ws, 89, col, parseBorderSpec("----"));
  }
  setGrey(ws, 89, COL_N, parseBorderSpec("---m"));
  ws.getRow(89).height = 30;

  VALUE_CHAIN_ROWS.forEach(([label, key], idx) => {
    const row = 90 + idx;
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: parseBorderSpec("tmt-"),
    });
    const raw = leadership?.[key];
    const pct =
      typeof raw === "object" && raw !== null
        ? formatRatio(raw.percentage ?? raw.plantsOffices ?? raw.value)
        : formatRatio(raw);
    mergeBlock(ws, row, COL_E, row, COL_G, pct, {
      align: "center",
      fill: VALUE_FILL,
      bordered: false,
      border: parseBorderSpec("ttt-"),
    });
    for (let col = COL_H; col < COL_N; col += 1) {
      setGrey(ws, row, col, parseBorderSpec("----"));
    }
    setGrey(ws, row, COL_N, parseBorderSpec("---m"));
    ws.getRow(row).height = 20.15;
  });

  setCell(ws, 95, COL_D, "Others – please specify", {
    align: "left",
    bordered: false,
    border: parseBorderSpec("tm--"),
  });
  setCell(ws, 95, COL_E, "Add Details", {
    align: "center",
    link: `#'${OTHER_VALUE_CHAIN_SHEET}'!D8`,
    bordered: false,
    border: parseBorderSpec(THIN_ALL),
  });
  for (let col = COL_F; col < COL_N; col += 1) {
    setGrey(ws, 95, col, parseBorderSpec("----"));
  }
  setGrey(ws, 95, COL_N, parseBorderSpec("---m"));
  ws.getRow(95).height = 20.15;

  // —— R96 L5 ——
  {
    const specs = outerMergeBorders(COL_J, {
      first: "tmtt",
      middle: "-ttt",
      last: THIN_ALL,
      edge: "---m",
    });
    writeMergedLabel(
      ws,
      96,
      COL_D,
      COL_J,
      "5. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 4 above.",
      specs,
      { align: "left", height: 30 }
    );
    setPlain(
      ws,
      96,
      COL_K,
      safeText(leadership?.correctiveActionTakenUnderway),
      parseBorderSpec(THIN_ALL),
      "left"
    );
    mergeBlock(ws, 96, COL_L, 96, COL_N, "", {
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec("-t--"),
    });
    ws.getCell(96, COL_N).border = parseBorderSpec("---m");
  }

  // —— R97 Notes ——
  {
    const specs = ["tmmt", "ttmt", "ttmt", "ttmt", "ttmt", "ttmt", "ttmt", "ttmt", "-tm-", "--m-", "--mm"];
    writeMergedLabel(ws, 97, COL_D, COL_J, "Notes", specs, {
      align: "left",
      height: 30,
    });
    setPlain(ws, 97, COL_K, safeText(principle?.notes), parseBorderSpec("ttmt"), "left");
    mergeBlock(ws, 97, COL_L, 97, COL_N, "", {
      fill: NA_FILL,
      bordered: false,
      border: parseBorderSpec("-tm-"),
    });
    ws.getCell(97, COL_N).border = parseBorderSpec("--mm");
  }

  return ws;
}
